import { useCallback, useEffect, useState } from 'react';

import { useLoginState } from '@xing-com/hub';

type TCFVendor = {
  id: number;
  name: string;
  consent: boolean | null;
};

type V3CmpController = {
  tcf: {
    vendors: Record<number, TCFVendor>;
  };
};

declare global {
  interface Window {
    __ucCmp?: {
      cmpController?: V3CmpController;
    };
  }
}

// https://usercentrics.com/docs/web/features/events/uc-ui-cmp-event/
type EventDetail = {
  source: 'none' | 'button' | 'first' | 'second' | 'embeddings' | '__ucCmp';
  type:
    | 'CMP_SHOWN'
    | 'ACCEPT_ALL'
    | 'DENY_ALL'
    | 'SAVE'
    | 'MORE_INFORMATION_LINK'
    | 'IMPRINT_LINK'
    | 'PRIVACY_POLICY_LINK';
};

const YIELDLOVE_VENDOR_ID = 251; // YieldLove GmbH

const getMarketingConsent = async (): Promise<boolean> => {
  try {
    if (window.__ucCmp?.cmpController) {
      const tcfVendors = window.__ucCmp.cmpController.tcf.vendors;
      return tcfVendors[YIELDLOVE_VENDOR_ID]?.consent === true;
    }
    return false;
  } catch {
    return false;
  }
};

export const useDisplayAds = (): { displayAdsAllowed: boolean } => {
  const isLoggedOut = useLoginState() === 'LOGGED_OUT';
  const [hasMarketingConsent, setHasMarketingConsent] = useState(false);

  const isDebugMode =
    global?.window?.localStorage?.getItem('xam_display_ads_debug') === 'true';

  const displayAdsAllowed =
    (isLoggedOut && isDebugMode) || (isLoggedOut && hasMarketingConsent);

  const updateMarketingConsent = useCallback((): void => {
    getMarketingConsent().then((value) => setHasMarketingConsent(value));
  }, []);

  const handleCmpEvent = useCallback(
    (event: Event): void => {
      const customEvent = event as CustomEvent<EventDetail>;
      const eventType = customEvent.detail?.type;
      if (
        eventType === 'SAVE' ||
        eventType === 'ACCEPT_ALL' ||
        eventType === 'DENY_ALL'
      ) {
        updateMarketingConsent();
      }
    },
    [updateMarketingConsent]
  );

  useEffect(() => {
    window.addEventListener('UC_UI_INITIALIZED', updateMarketingConsent);
    window.addEventListener('UC_UI_CMP_EVENT', handleCmpEvent);

    return () => {
      window.removeEventListener('UC_UI_INITIALIZED', updateMarketingConsent);
      window.removeEventListener('UC_UI_CMP_EVENT', handleCmpEvent);
    };
  }, [updateMarketingConsent, handleCmpEvent]);

  return {
    displayAdsAllowed,
  };
};
